import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useProtocol } from '@/hooks/useProtocol'

const CROSS_PROTOCOL_STORAGE_KEY = 'mackinac:crossProtocol'
const CROSS_PROTOCOL_ACCOUNTS_KEY = 'mackinac:crossProtocolAccounts'

interface CrossProtocolContextType {
    showAllAccounts: boolean
    setShowAllAccounts: (v: boolean) => void
    enabledAccountIds: string[]
    setEnabledAccountIds: (ids: string[]) => void
}

const CrossProtocolContext = createContext<CrossProtocolContextType | null>(null)

export function CrossProtocolProvider({ children }: { children: ReactNode }) {
    const { connectedAccounts } = useProtocol()

    const [showAllAccounts, setShowAllAccountsState] = useState(() => {
        try {
            return localStorage.getItem(CROSS_PROTOCOL_STORAGE_KEY) === 'true'
        } catch {
            return false
        }
    })

    const [enabledAccountIds, setEnabledAccountIdsState] = useState<string[]>(() => {
        try {
            return JSON.parse(localStorage.getItem(CROSS_PROTOCOL_ACCOUNTS_KEY) ?? '[]')
        } catch {
            return []
        }
    })

    // useCallback so consumers can safely list this in their own effect/callback deps
    // without it forcing a re-run on every render.
    const setShowAllAccounts = useCallback((v: boolean) => {
        try {
            if (v) {
                localStorage.setItem(CROSS_PROTOCOL_STORAGE_KEY, 'true')
            } else {
                localStorage.removeItem(CROSS_PROTOCOL_STORAGE_KEY)
            }
        } catch {
            /* localStorage unavailable */
        }
        setShowAllAccountsState(v)
    }, [])

    const setEnabledAccountIds = (ids: string[]) => {
        try {
            localStorage.setItem(CROSS_PROTOCOL_ACCOUNTS_KEY, JSON.stringify(ids))
        } catch {
            /* localStorage unavailable */
        }
        setEnabledAccountIdsState(ids)
    }

    // Deliberately reacts to connectedAccounts only, not enabledAccountIds: this validates
    // and prunes stale ids whenever the connected-account set changes. Adding
    // enabledAccountIds here would also re-run this on every direct setEnabledAccountIds call
    // (e.g. from the settings UI) — including before connectedAccounts has loaded, when it's
    // still `[]`, which would wipe out ids that were just set rather than merely pruning
    // stale ones.
    useEffect(() => {
        if (enabledAccountIds.length === 0) return
        const validIds = new Set(connectedAccounts.map(a => a.id))
        const filtered = enabledAccountIds.filter(id => validIds.has(id))
        if (filtered.length !== enabledAccountIds.length) {
            setEnabledAccountIds(filtered)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedAccounts])

    return (
        <CrossProtocolContext.Provider
            value={{ showAllAccounts, setShowAllAccounts, enabledAccountIds, setEnabledAccountIds }}
        >
            {children}
        </CrossProtocolContext.Provider>
    )
}

export function useCrossProtocol() {
    const ctx = useContext(CrossProtocolContext)
    if (!ctx) throw new Error('useCrossProtocol must be used within CrossProtocolProvider')
    return ctx
}
